/**
 * A110W « Commander sur WhatsApp » in two steps — pure state (no DOM).
 *
 * A web page cannot attach a picture to a wa.me link, so the order goes in two
 * steps: 1) the prefilled message opens the chat with Maison Mata; 2) the
 * customer sends the try-on photo (system share sheet, or a download to attach
 * by hand). Step 1 is « done » as soon as WhatsApp was opened — we never know
 * whether the message was actually sent.
 *
 * Lifetime: step 1 stays done while the panel is open for the same pair. A new
 * model on the face (another pair) or closing the panel starts again at step 1.
 * A new photo (« Reprendre ») keeps step 1 done: step 2 simply sends the new photo.
 */

export type OrderStep = 'message' | 'photo';

export interface OrderFlow {
  readonly step: OrderStep;
  /** Frame the WhatsApp message was prepared for (null at step 1). */
  readonly frameId: string | null;
}

export type OrderFlowEvent =
  /** « Commander sur WhatsApp » (or « Rouvrir ») opened the chat for this frame. */
  | { type: 'whatsapp'; frameId: string }
  /** The model on the face changed (or was reloaded). */
  | { type: 'frame'; frameId: string }
  /** The panel was closed (mobile) or another tab was chosen (desktop). */
  | { type: 'hide' }
  /** A new photo was taken. */
  | { type: 'photo' };

export const INITIAL_ORDER_FLOW: OrderFlow = Object.freeze({ step: 'message', frameId: null });

export function orderFlow(state: OrderFlow, event: OrderFlowEvent): OrderFlow {
  switch (event.type) {
    case 'whatsapp':
      return state.step === 'photo' && state.frameId === event.frameId ? state : { step: 'photo', frameId: event.frameId };
    case 'frame':
      return state.step === 'photo' && state.frameId !== event.frameId ? INITIAL_ORDER_FLOW : state;
    case 'hide':
      return INITIAL_ORDER_FLOW;
    case 'photo':
      return state;
  }
}

/** How step 2 hands over the photo: the share sheet when it takes the file, otherwise a download. */
export type PhotoHandOff = 'share' | 'download';

/**
 * What the step 2 button shows. `preparing` while the JPEG is not ready yet:
 * the share sheet must open within the tap (iOS user activation), so the file
 * has to exist before the customer can press the button.
 */
export function sendButtonState(handOff: PhotoHandOff, fileReady: boolean, hasPhoto: boolean, capturing: boolean): { label: 'send' | 'save' | 'preparing'; enabled: boolean } {
  // No photo and none on its way (capture failed): the button stays usable and asks for a new photo.
  if (!fileReady && (hasPhoto || capturing)) return { label: 'preparing', enabled: false };
  return { label: handOff === 'share' ? 'send' : 'save', enabled: true };
}
